import { GEWERK_TEXT, uhr } from '../api.ts';
import {
  heuteTag, istWochenende, jahrVon, kurzTag, kwVon, wochentag, wochentagNr,
  type Tag,
} from './rastertage.ts';

/**
 * Der Wochenplan als ZEITRASTER, eine Woche je Block.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „der wochenplan ist ja ganz schlimm so kann er nicht bleiben. warum der
 *    auch hier grau ist weiß kein mensch. lieber in dem aufbau wie bild 2.
 *    auch die KW gehört darein."
 *
 * WAS FALSCH WAR, WAR NICHT DIE FARBE, SONDERN DIE REIHENFOLGE. Die Tage
 * waren Listen: Sperre, Sperre, Sperre, Sperre, Sperre, Termin, Termin. An
 * einem Mittwoch mit neun Terminen standen sechzehn graue Zeilen darüber, und
 * die grauen sind das, was NICHT stattfindet. Der Plan zeigte in erster Linie
 * Abwesenheit.
 *
 * FRÜHER WAR ES EIN RASTER, und das ist der ganze Unterschied: die Zeit
 * steht links, jeder Termin hat seinen PLATZ und seine HÖHE, und man sieht in
 * einem Blick, wann am Dienstag noch nichts liegt. Eine Liste beantwortet
 * „was ist an dem Tag"; ein Raster beantwortet „wann kann ich noch".
 *
 * UND DIE SPERREN SIND GANZ WEG (18.09.2026). Sie lagen schraffiert hinter
 * dem Raster und standen als Zahl im Tageskopf.
 *
 *   ORIGINALTEXT MARVIN (18.09.2026)
 *     „die sperre bei den terminen muss raus. diese wirkt sich auch auf den
 *      wochenplan aus. ... entweder er ist gebucht oder nicht."
 *
 * **Ein Raster, das eine Fläche sperrt, ohne dass jemand dort etwas vorhat,
 * behauptet eine Auslastung, die es nicht gibt.** Was ein ganzer Tag nicht
 * kann, meldet die Firma über die Verfügbarkeit; die steht weiterhin im
 * Tageskopf.
 *
 * DIE KALENDERWOCHE STEHT ÜBER JEDER WOCHE. Sie ist die Einheit, in der auf
 * der Baustelle geredet wird („die KW 36 ist voll"), und sie stand in diesem
 * Plan nirgends.
 */

/* DIESES RASTER TRAEGT KEINE `Date`-OBJEKTE MEHR, und das ist die Behebung
   vom 10.09.2026.
   ==========================================================================
   ORIGINALTEXT MARVIN (mit 2 Bildschirmfotos)
     „bild 1: von kamil, deutsche zeitzone, bild 2: mein pc, zypern
      zeitzone. aber das problem sind die wochentage, datum."

   DIE TAGE WURDEN AUF DER UHR DES BETRACHTERS GEBAUT (`setHours(0,0,0,0)`,
   `getDate()`, `toLocaleDateString`) und auf der Uhr des BETRIEBS gelesen
   (`tagIso`). Mitternacht am 07.09. in Zypern ist in Berlin der 06.09. um
   23:00 - unter „Montag 07.09." standen deshalb die Termine vom Sonntag,
   jeder Termin eine Spalte zu weit links.

   > **Ein Wert, den 2 Uhren lesen, ist 2 Werte.** Solange beide Uhren
   > dieselbe Zeit zeigen, ist es EIN Fehler, den niemand sieht.

   Ein Tag ist jetzt „2026-09-07" und sonst nichts. Beschriftung und
   Datenzugriff kommen aus DERSELBEN Zeichenkette; was aus einer Zeichenkette
   kommt, kann nicht auseinanderlaufen. Die Regeln stehen in
   `teile/rastertage.ts` und werden in Pruefstand 022 gemessen - der laeuft
   ausdruecklich in Marvins Zone. */
const kw = kwVon;
const kurz = kurzTag;

/** „09:30" wird zu 570. Fehlt die Zeit, ist es `null`. */
function minuten(t: unknown): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t ?? ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Wie hoch eine halbe Stunde im Raster ist. */
const ZEILE = 26;
/* SO LANG STEHT EIN TERMIN MINDESTENS DA (.278). Marvin, 02.10.2026: „hier
   wird die spalten nicht vergrößert damit man die einträge lesen kann." Ein
   Termin von 30 Minuten war eine Zeile hoch: Zeit ja, Straße und KLS nein.
   Gezeichnet wird er jetzt mindestens 75 Minuten hoch (3 Zeilen Text), und
   was sich dadurch überschneidet, steht nebeneinander - der Tag wird dann
   breiter (siehe `bahnenJeTag`). */
const MINDEST_MINUTEN = 75;
/** Das gezeichnete Ende: das echte, aber nie kürzer als `MINDEST_MINUTEN`. */
function gezeichnetBis(x: any): number {
  const a = minuten(x.termin_von)!;
  const e = minuten(x.termin_bis) ?? (a + 60);
  return Math.max(e, a + MINDEST_MINUTEN);
}
/** Die Randzeiten des Rasters, wenn nichts anderes darin liegt. */
const VON_STANDARD = 7 * 60;
const BIS_STANDARD = 18 * 60;

export interface Rasterlage {
  /** Alle Tage des Zeitraums, aufsteigend. */
  tage: Tag[];
  termineAm: (t: Tag) => any[];
  /** ALLE Verfügbarkeitsmeldungen des Tages, nicht nur der Urlaub. */
  gemeldetAm: (t: Tag) => any[];
  /** Wie viele Firmen es überhaupt gibt (nach dem Filter). */
  firmenAktiv: number;
  waehle: (x: any) => void;
  /**
   * OHNE LAGE, SOLANGE EINE SUCHE LÄUFT.
   *
   * ORIGINALTEXT MARVIN (08.09.2026)
   *   „den gesamtkalender nur mit dem gesuchten auftrag anszeigen, wo er
   *    terminiert ist, alle anderen einträge werden ausgeblendet"
   *
   * Der Server liefert dann keinen Urlaub - und `lageVon` würde
   * daraus „nicht gemeldet" machen, an JEDEM Tag. **Das ist eine Aussage über
   * die Firmen, während in Wahrheit gar nicht gefragt wurde**, und sie sieht
   * aus wie eine Messung: genau die Sorte Zahl, wegen der der Tageskopf am
   * 26.08.2026 schon einmal umgebaut werden musste. Also steht dort während
   * einer Suche NICHTS, und der Kasten über dem Plan sagt, warum.
   */
  ohneLage?: boolean;
}

/**
 * WAS AN EINEM TAG WIRKLICH GEMELDET IST.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „wieso zeigtst du zb bei los den 27.08. und 28.08. als verfügbar an,
 *    obwohl er laut verfügbarkeitskalender nicht verfügbar ist."
 *
 * DER TAGESKOPF SAGTE „verfügbar", WENN KEIN URLAUB EINGETRAGEN WAR. Das ist
 * nicht dasselbe. `verfuegbarkeit` hat eine Zeile je Firma und Tag; wo keine
 * steht, hat die Firma NICHTS GEMELDET - und das ist eine dritte Auskunft,
 * nicht die erste.
 *
 * „Nicht gemeldet" heisst: niemand weiss es, und wer darauf bucht, ruft am
 * Vorabend an. „Verfügbar" heisst: die Firma hat gesagt, dass sie kann. Der
 * Unterschied ist der ganze Zweck dieses Kalenders, und der Bildschirm hat
 * ihn eingeebnet - auf die beruhigende Seite.
 */
function lageVon(gemeldet: any[], firmen: number):
  { wort: string; klasse: string; titel: string } {
  const nein = gemeldet.filter((v) => v.zustand !== 'verfuegbar');
  const ja = gemeldet.filter((v) => v.zustand === 'verfuegbar');

  const zeile = (v: any) =>
    `${v.firma}: ${v.zustand === 'urlaub' ? 'Urlaub'
                 : v.zustand === 'verfuegbar' ? 'verfügbar'
                 : 'nicht verfügbar'}`
    + (v.hinweis ? ` (${v.hinweis})` : '');

  if (nein.length) {
    const nurUrlaub = nein.every((v) => v.zustand === 'urlaub');
    /* DAS GANZE FELD, ABER NUR WENN ES FÜR ALLE GILT.
     *
     * ORIGINALTEXT MARVIN (02.09.2026)
     *   „und beim urlaub dann das gesamte feld einfärben wie bei verfügbar.
     *    nur bei der sammelansicht angepasst lassen."
     *
     * „Verfügbar" färbt seit jeher die ganze Kopfzelle, „Urlaub" färbte nur
     * das Wort - und damit sah ein gesperrter Tag fast aus wie ein freier.
     * Das GANZE Feld darf aber nur dann rot werden, wenn niemand an diesem
     * Tag kann: in der Sammelansicht arbeiten die übrigen Firmen weiter, und
     * eine rote Spalte hiesse dort „hier geht nichts", was falsch wäre. Also
     * genau dann, wenn KEINE der gezeigten Firmen verfügbar gemeldet hat.
     */
    const alleWeg = ja.length === 0 && (firmen <= 1 || nein.length >= firmen);
    return {
      wort: firmen === 1
        ? (nurUrlaub ? 'Urlaub' : 'nicht verfügbar')
        : `${nein.length} ${nurUrlaub ? 'im Urlaub' : 'nicht verfügbar'}`,
      klasse: 'urlaub' + (alleWeg ? ' ganz' : ''),
      titel: gemeldet.map(zeile).join('\n'),
    };
  }
  if (ja.length) {
    return {
      wort: firmen === 1 || ja.length >= firmen
        ? 'verfügbar' : `${ja.length} von ${firmen} verfügbar`,
      klasse: 'frei',
      titel: ja.map(zeile).join('\n'),
    };
  }
  /* NICHTS GEMELDET IST NICHT VERFÜGBAR. Es ist die dritte Auskunft, und sie
     ist genau die, auf die jemand anrufen muss. */
  return {
    wort: 'nicht gemeldet', klasse: 'offen',
    titel: firmen === 1
      ? 'Diese Firma hat für den Tag nichts gemeldet.'
      : `Keine der ${firmen} Firmen hat für diesen Tag etwas gemeldet.`,
  };
}

/**
 * Die Tage in Wochenblöcke schneiden.
 *
 * DAS WOCHENENDE FÄLLT WEG, WENN NICHTS DARIN LIEGT. Zwei leere Spalten in
 * jeder Woche kosten ein Fünftel der Breite, und auf der Baustelle wird
 * samstags nur im Ausnahmefall gearbeitet. Liegt dort etwas, steht es da -
 * ein Termin, den das Raster verschluckt, ist schlimmer als eine leere Spalte.
 */
function wochen(tage: Tag[], hatWas: (t: Tag) => boolean): Tag[][] {
  const raus: Tag[][] = [];
  let jetzt: Tag[] = [];
  for (const t of tage) {
    /* `wochentagNr` ZAEHLT AB MONTAG. `Date.getDay()` faengt am Sonntag an,
       und die Umrechnung `(getDay() + 6) % 7` stand 6-mal in dieser
       Oberflaeche. */
    if (jetzt.length && wochentagNr(t) === 0) { raus.push(jetzt); jetzt = []; }
    jetzt.push(t);
  }
  if (jetzt.length) raus.push(jetzt);
  return raus.map((w) => w.filter((t) => !istWochenende(t) || hatWas(t)));
}


export default function Wochenraster({
  tage, termineAm, gemeldetAm, firmenAktiv, waehle, ohneLage,
}: Rasterlage) {
  const hatWas = (t: Tag) =>
    termineAm(t).length > 0 || gemeldetAm(t).length > 0;
  const bloecke = wochen(tage, hatWas).filter((w) => w.length > 0);

  return (
    <>
      {bloecke.map((woche) => (
        <Wochenblock
          key={woche[0]!} woche={woche}
          termineAm={termineAm} gemeldetAm={gemeldetAm}
          firmenAktiv={firmenAktiv} waehle={waehle} ohneLage={ohneLage} />
      ))}
      {bloecke.length === 0 && (
        <p className="klein leise">In diesem Zeitraum steht nichts.</p>
      )}
    </>
  );
}


function Wochenblock({ woche, termineAm, gemeldetAm, firmenAktiv,
                       waehle, ohneLage }: {
  woche: Tag[];
  termineAm: (t: Tag) => any[];
  gemeldetAm: (t: Tag) => any[];
  firmenAktiv: number;
  waehle: (x: any) => void;
  ohneLage?: boolean;
}) {
  /* WIE FRÜH UND WIE SPÄT DAS RASTER REICHT, entscheidet die Woche selbst.
     Ein festes 07:00-18:00 verschluckt den Termin um 06:30 - und genau der
     ist der, den jemand suchen wird. Der Standard gilt nur, solange nichts
     darüber hinausgeht. */
  let von = VON_STANDARD, bis = BIS_STANDARD;
  for (const t of woche) {
    for (const x of termineAm(t)) {
      const a = minuten(x.termin_von); const e = minuten(x.termin_bis);
      if (a !== null) von = Math.min(von, Math.floor(a / 30) * 30);
      if (e !== null) bis = Math.max(bis, Math.ceil(e / 30) * 30);
      if (a !== null && e === null) bis = Math.max(bis, a + 60);
    }
  }
  const schritte: number[] = [];
  for (let m = von; m < bis; m += 30) schritte.push(m);
  const hoehe = schritte.length * ZEILE;

  /* „HEUTE" IST WIRKLICH EINE FRAGE AN EINE UHR - und zwar an die des
     BETRIEBS. Wer in Zypern um 00:30 Ortszeit auf den Plan sieht, arbeitet in
     einem Betrieb, in dem noch der Vortag laeuft, und der Rahmen gehoert um
     den Tag, an dem die Leute draussen stehen. */
  const heute = heuteTag();
  const erster = woche[0]!;
  const letzter = woche[woche.length - 1]!;

  /* OHNE UHRZEIT IST KEIN PLATZ IM RASTER. Ein Termin, dessen Zeit noch nicht
     feststeht, darf nicht auf 00:00 rutschen: dort steht er ganz oben und
     sieht aus wie der erste des Tages. Er bekommt eine eigene Zeile über dem
     Raster - sichtbar, und ohne eine Uhrzeit zu behaupten. */
  const ohneZeit = (t: Tag) =>
    termineAm(t).filter((x) => minuten(x.termin_von) === null);
  const mitZeit = (t: Tag) =>
    termineAm(t).filter((x) => minuten(x.termin_von) !== null);

  return (
    <div className="wochenblock">
      <div className="wochenkopf">
        <span className="kwmarke">KW {kw(erster)}</span>
        <b>{kurz(erster)} bis {kurz(letzter)}{jahrVon(letzter)}</b>
        <div className="wachsen" />
        <span className="klein leise">
          {(() => {
            const n = woche.reduce((s2, t) => s2 + termineAm(t).length, 0);
            return n === 1 ? '1 Termin' : `${n} Termine`;
          })()}
        </span>
      </div>

      <div className="raster"
           style={{ gridTemplateColumns: `56px ${woche.map((t) => `minmax(0, ${bahnenJeTag(mitZeit(t))}fr)`).join(' ')}` }}>
        {/* --- Kopfzeile: der Tag und woran man ist ------------------------ */}
        <div className="rasterecke" />
        {woche.map((t) => {
          const gemeldet = gemeldetAm(t);
          const anzahl = termineAm(t).length;
          /* WAS WIRKLICH GEMELDET IST, in einem Wort. Und wo NICHTS gemeldet
             ist, steht „nicht gemeldet" - nicht „verfügbar". */
          const lage = lageVon(gemeldet, firmenAktiv);
          return (
            <div key={t}
                 className={'rasterkopf ' + (ohneLage ? 'offen' : lage.klasse)
                          + (t === heute ? ' heute' : '')}>
              {/* DER WOCHENTAG KAM AUS `toLocaleDateString` - also aus der Uhr
                  UND der Sprache des Betrachters. Er kommt jetzt aus dem Tag
                  selbst, gerechnet und auf Deutsch. */}
              <b>{wochentag(t)} {kurz(t)}</b>
              {/* WÄHREND EINER SUCHE STEHT HIER NICHTS. Der Grund steht an
                  `ohneLage` in `Rasterlage`. */}
              {!ohneLage && (
                <span className="lage" title={lage.titel}>{lage.wort}</span>
              )}
              {anzahl > 0 && <span className="zahl">{anzahl}</span>}
            </div>
          );
        })}

        {/* --- Termine ohne Uhrzeit ---------------------------------------- */}
        {woche.some((t) => ohneZeit(t).length > 0) && (
          <>
            <div className="rasterzeit ohnezeit">ohne Zeit</div>
            {woche.map((t) => (
              <div key={'o' + t} className="rasterspalte ohnezeit">
                {ohneZeit(t).map((x: any) => (
                  <Terminblock key={x.ids.join('+')} x={x} waehle={waehle} />
                ))}
              </div>
            ))}
          </>
        )}

        {/* --- Die Zeitspalte ---------------------------------------------- */}
        <div className="rasterzeit" style={{ height: hoehe }}>
          {schritte.map((m) => (
            <div key={m} className={'stunde' + (m % 60 ? ' halb' : '')}
                 style={{ height: ZEILE }}>
              {m % 60 === 0
                ? `${String(Math.floor(m / 60)).padStart(2, '0')}:00`
                : ''}
            </div>
          ))}
        </div>

        {/* --- Ein Tag ------------------------------------------------------ */}
        {woche.map((t) => (
          <div key={'s' + t}
               className={'rasterspalte' + (t === heute ? ' heute' : '')}
               style={{ height: hoehe }}>
            {/* Die Linien liegen ganz hinten, damit alles andere darauf liegt. */}
            {schritte.map((m, i) => (
              <div key={m} className={'linie' + (m % 60 ? ' halb' : '')}
                   style={{ top: i * ZEILE, height: ZEILE }} />
            ))}

            {legeNebeneinander(mitZeit(t)).map(({ x, spalte, spalten }) => {
              const a = minuten(x.termin_von)!;
              const e = gezeichnetBis(x);
              const oben = ((Math.max(a, von) - von) / 30) * ZEILE;
              const hoch = Math.max(ZEILE - 2,
                ((Math.min(e, bis) - Math.max(a, von)) / 30) * ZEILE - 2);
              return (
                <Terminblock
                  key={x.ids.join('+')} x={x} waehle={waehle}
                  stil={{
                    position: 'absolute', top: oben, height: hoch,
                    left: `calc(${(spalte / spalten) * 100}% + 2px)`,
                    width: `calc(${(1 / spalten) * 100}% - 4px)`,
                  }} />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}


/** Wie viele Termine an einem Tag nebeneinander stehen (höchstens 3): so
    viel breiter wird der Tag, damit jeder lesbar bleibt (.278). */
function bahnenJeTag(liste: any[]): number {
  return Math.min(3, Math.max(1, ...legeNebeneinander(liste).map((g) => g.spalten)));
}

/**
 * Was sich überschneidet, steht NEBENEINANDER statt übereinander.
 *
 * Zwei Termine von 09:00 bis 14:00 am selben Tag würden sonst genau
 * deckungsgleich liegen, und der zweite wäre unsichtbar. Ein Plan, der einen
 * Termin verschluckt, ist schlimmer als gar keiner: er sieht vollständig aus.
 */
function legeNebeneinander(liste: any[]):
  { x: any; spalte: number; spalten: number }[] {
  const sortiert = [...liste].sort((a, b) =>
    (minuten(a.termin_von) ?? 0) - (minuten(b.termin_von) ?? 0));

  const raus: { x: any; spalte: number; spalten: number }[] = [];
  let gruppe: typeof raus = [];
  let gruppenEnde = -1;

  const abschliessen = () => {
    const n = Math.max(1, ...gruppe.map((g) => g.spalte + 1));
    for (const g of gruppe) g.spalten = n;
    gruppe = []; gruppenEnde = -1;
  };

  for (const x of sortiert) {
    const a = minuten(x.termin_von)!;
    const e = gezeichnetBis(x);
    if (gruppe.length && a >= gruppenEnde) abschliessen();

    const belegt = new Set(gruppe
      .filter((g) => gezeichnetBis(g.x) > a)
      .map((g) => g.spalte));
    let spalte = 0;
    while (belegt.has(spalte)) spalte++;

    const eintrag = { x, spalte, spalten: 1 };
    gruppe.push(eintrag); raus.push(eintrag);
    gruppenEnde = Math.max(gruppenEnde, e);
  }
  if (gruppe.length) abschliessen();
  return raus;
}


function Terminblock({ x, waehle, stil }:
                     { x: any; waehle: (x: any) => void; stil?: any }) {
  return (
    <div
      className={'termin g-' + x.arten[0] + (x.erledigt ? ' fertig' : '')}
      style={stil}
      onClick={() => waehle(x)}
      title={`${x.arten.map((a: string) => GEWERK_TEXT[a]).join(' und ')}`
           + (x.bautage > 1 ? `, Tag ${x.tag_nr} von ${x.bautage}` : '')
           + `, ${x.firma ?? 'keine Firma'}`
           + `, ${x.strasse} ${x.hausnummer}${x.hausnummer_zusatz ?? ''}, `
           + `${x.plz ?? ''} ${x.ort_text ?? ''}`}
    >
      {/* EIN MEHRTÄGIGER TIEFBAU STEHT AN JEDEM SEINER TAGE. Ohne die Zahl
          sähen drei Tage aus wie drei Termine an derselben Baustelle - und
          jemand sagt zwei davon ab. */}
      <div className="zeit">
        {x.termin_von ? uhr(x.termin_von) : 'ohne Zeit'} {x.arten.join('+')}
        {x.bautage > 1 ? ` ${x.tag_nr}/${x.bautage}` : ''}
        {x.erledigt && ' ✓'}
      </div>
      <div className="ort">
        {x.strasse} {x.hausnummer}{x.hausnummer_zusatz ?? ''}
      </div>
      <div className="ortzusatz">
        {x.kls_id ? `KLS ${x.kls_id}, ` : ''}{x.ort_text}
      </div>
      {!x.firma && <div className="firma">keine Firma</div>}
    </div>
  );
}
